
import { Options, Vue } from 'vue-class-component';
import ModalBox from '@/shared/components/modal-box/index.vue';
import { Inject, Prop } from 'vue-property-decorator';
import { TrustAPI } from '@/shared/modules/trust-api';
import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import { EventEmitter } from 'events';
import store from '@/trust-admin-app/store';
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import FileDragAndDrop from '@/shared/components/file-drag-and-drop/index.vue';
import V3DatePicker from 'vue3-datepicker';
import AlertBox from '@/shared/components/alert-box/index.vue';
import { CorrespondenceDescription, CorrespondenceRequest, CorrespondenceType, CorrespondenceView } from '@/shared/modules/typescript-trustapi-client/models';
import moment from 'moment';


@Options({
  components: {
    ModalBox,
    LoadingIndicator,
    ErrorIndicator,
    FileDragAndDrop,
    V3DatePicker,
    AlertBox
  },
  mixins: [FormatDateTimeMixin]
})
export default class AddEditCorrespondenceModal extends Vue {

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Inject({ from: 'correspondenceType', default: {} })
  private correspondenceType!: CorrespondenceType[];

  @Inject({ from: 'correspondenceDescriptions', default: {} })
  private correspondenceDescriptions!: CorrespondenceDescription[];

  @Prop({ default: {} })
  public correspondence!: CorrespondenceView;

  private formData: CorrespondenceView = null as any;
  private fileList: Array<File> = [];
  private dateReceived: Date = null as any;

  private submitting = false;
  private allowFileOverwrite = true;
  private showDeleteAlert = false;
  private acceptAnyFileTypes = true;

  created(): void {
    this.formData = JSON.parse(JSON.stringify(this.correspondence));
    this.formData.id = this.formData.id ?? 0;
    this.formData.correspondenceType = this.formData.correspondenceType ?? null as any;
    this.formData.correspondenceDescription = this.formData.correspondenceDescription ?? null as any;
    this.formData.description = this.formData.description ?? null as any;
    this.dateReceived = this.formData.dateReceived ? new Date(this.formData.dateReceived) : new Date();
    this.allowFileOverwrite = this.formData.file ?? true;
  }

  private correspondenceChanged(files: Array<File>) {
    this.fileList = files.length > 0 ? files : null as any;
  }

  private async saveCorrespondence(): Promise<void> {
    try {

      this.submitting = true;
      const correspondenceReq: CorrespondenceRequest = {
        id: this.formData.id,
        practiceId: this.formData.practiceId,
        correspondenceTypeId: this.formData.correspondenceType.id,
        correspondenceDescriptionId: this.formData.correspondenceDescription.id,
        dateReceived: this.dateReceived,
        description: this.formData.correspondenceDescription.id == 13 ? this.formData.description : null,
      }
      if (this.fileList.length == 1) {
        correspondenceReq.fileDataUrl = await this.fileToString(this.fileList[0]);
        correspondenceReq.fileName = this.fileList[0].name;
        correspondenceReq.fileType = this.fileList[0].type || 'application/octet-stream';
      }
      await this.apiService.CorrespondenceApi?.apiV2CorrespondenceSaveCorrespondencePost(correspondenceReq);

      if (this.formData.id == 0) {
        this.eventCtrl.emit('show-snackbar', 'success', 'The correspondence has been added.');
      } else {
        this.eventCtrl.emit('show-snackbar', 'success', 'The correspondence has been updated');
      }

      this.eventCtrl.emit('reload-practice-correspondence');
      this.closeModal();

    } catch (err) {
      if (this.formData.id == 0) {
        this.eventCtrl.emit('show-snackbar', 'error', 'Problem adding correspondence.');
      } else {
        this.eventCtrl.emit('show-snackbar', 'error', 'Problem updating correspondence.');
      }
    } finally {
      this.submitting = false;
    }

  }


  private fileToString(file: any): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        return resolve(reader.result as string);
      };

      reader.onabort = () => {
        return reject();
      }

      reader.onerror = () => {
        return reject();
      }
    });
  }

  private yesClicked(): void {
    this.deleteCorrespondence();
  }

  private async deleteCorrespondence(): Promise<void> {
    try {
      this.submitting = true;
      await this.apiService.CorrespondenceApi?.apiV2CorrespondenceDeleteCorrespondenceCorrespondenceIdDelete(
        this.formData.id
      );
      this.eventCtrl.emit('reload-practice-correspondence');
      this.eventCtrl.emit('show-snackbar', 'success', 'Correspondence deleted successfully');
      this.closeModal();
    } catch {
      this.eventCtrl.emit('show-snackbar', 'error', 'Problem deleting correspondence');
    } finally {
      this.submitting = false;
    }
  }



  get validForm(): boolean {
    const chk1 = !!this.formData.correspondenceType;
    const chk2 = !!this.dateReceived;
    const chk3 = !!this.formData.description;
    const chk4 = this.fileList?.length == 1;
    const chk5 = !this.formData.file && chk4;
    const chk6 = !!this.formData.file && !this.allowFileOverwrite && chk4;
    const chk7 = !!this.formData.file && this.fileList?.length == 0;
    const chk8 = !!this.formData.correspondenceDescription;
    const chk9 = !!this.formData.correspondenceDescription && this.formData.correspondenceDescription.id == 13;
    return this.editMode ? (chk1 && chk2 && ((chk8 && !chk9) || (chk8 && chk9 && chk3)) && (chk6 || chk7)) :
      (chk1 && chk2 && ((chk8 && !chk9) || (chk8 && chk9 && chk3)) && chk5);
  }

  get isFormDirty(): boolean {
    const chk1 = this.correspondence.correspondenceType?.id != this.formData.correspondenceType?.id;
    const chk2 = moment(moment(this.formData.dateReceived ?? this.dateReceived).format('YYYY-MM-DD')).unix() !=
        moment(moment(this.dateReceived).format('YYYY-MM-DD')).unix();
    const chk3 = this.formData.correspondenceDescription?.id == 13 && (this.correspondence.description != this.formData.description);
    const chk4 = this.fileList?.length == 1;
    const chk5 = this.correspondence.correspondenceDescription?.id != this.formData.correspondenceDescription?.id;
    return chk1 || chk2 || chk3 || chk4 || chk5;
  }


  get editMode(): boolean {
    return this.correspondence?.id > 0;
  }

  private closeModal() {
    this.$emit('closeAddEditCorrespondenceModal');
  }


  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

}

